import IstanbulApp from './components/images/istanbulapp.jpg';
import Blog from './components/images/blog.png';
import Nazif from './components/images/nazif_gungor.png';

/**
 * Single source of truth for all projects.
 * - `gallery`: array of image URLs; gallery[0] is the cover used in the grid.
 * - `links`: array of { type, url, label }. type ∈ web | github | ios | android | app
 */
const P = `${process.env.PUBLIC_URL}/project_images`;
const g = (...names) => names.map((n) => `${P}/${n}`);

const projects = [
  {
    slug: 'portfolio',
    title: 'Portfolio Website',
    company: 'Personal',
    category: 'Web',
    year: '2026',
    gallery: g('portfolio.png', 'portfolio2.png', 'portfolio3.png', 'portfolio4.png'),
    short:
      'The site you’re looking at — a modern, animated personal portfolio.',
    description:
      'The portfolio you’re browsing right now. A modern, fully responsive site with a dark/light alternating layout, a live theme-color switcher, scroll-triggered animations, a project gallery with lightbox, detail pages, an interactive experience timeline and a working contact form powered by Resend. Built with React, styled-components and Framer Motion.',
    tech: ['React', 'Styled Components', 'Framer Motion', 'Node.js', 'Resend'],
    links: [],
  },
  {
    slug: 'stl-teknoloji',
    title: 'STL Teknoloji',
    company: 'STL Teknoloji',
    category: 'Corporate Website',
    year: '2026',
    gallery: g('stl.png', 'stl2.png', 'stl3.png', 'stl4.png', 'stl5.png'),
    short:
      'Multi-brand corporate website for a manufacturer and its four consumer brands.',
    description:
      'A multi-brand corporate website for STL Teknoloji, an Istanbul-based manufacturer and the parent company of four consumer brands: Oxyra (gaming gear), Fressi (retro home appliances), wexta (luggage) and Beauty Net Korea (K-beauty). The home page opens with an interactive brand accordion, and each brand gets its own landing page built on its own visual identity (colors, typography, logo and tone of voice) while sharing one component system. I built it end to end: TR/EN i18n, a Supabase-backed admin panel that lets the client edit every piece of copy with version history, a GA4-based analytics dashboard, cookie consent, and static prerendering with a strong focus on SEO and Core Web Vitals (mobile LCP, preloading, redirects from the legacy site). Deployed on Vercel.',
    tech: ['React', 'TypeScript', 'Vite', 'Tailwind CSS', 'Supabase', 'Vercel'],
    links: [{ type: 'web', url: 'https://stlteknoloji.com/', label: 'Visit website' }],
  },
  {
    slug: 'avidef-prospecting',
    title: 'AVIDEF Prospecting Platform',
    company: 'AVIDEF',
    category: 'AI SaaS',
    year: '2025',
    gallery: g('prospect.png', 'prospect2.png', 'prospect3.png', 'prospect4.png'),
    short:
      'AI-powered prospecting & sales-intelligence platform for AVIDEF.',
    description:
      'An internal prospecting and sales-intelligence platform I built for AVIDEF. It centralizes the firm’s financial documents and intelligence on companies across the industry, runs AI-driven analysis, and builds sales pipelines. It can discover companies’ websites and email addresses, supports email marketing through custom mail templates, and provides an analytics dashboard to track outreach and performance.',
    tech: ['Next.js', 'TypeScript', 'Supabase', 'AI APIs', 'Tailwind CSS'],
    links: [],
  },
  {
    slug: 'nailing-ai',
    title: 'Nailing.ai',
    company: 'Nailing.ai',
    category: 'AI SaaS',
    year: '2025',
    gallery: g('nailing.png', 'nailing2.png', 'nailing3.png', 'nailing4.png'),
    short:
      'AI-powered content analysis & script generation platform (closed beta).',
    description:
      'Co-founded Nailing.ai — an AI-powered content analysis and script generation platform. I architected the full-stack infrastructure, designed the AI-based transcript analysis and hook-optimization system for short-form content, and built competitor analysis and content-structuring modules. Currently running a closed beta with 20–30 test users while leading the product roadmap and system architecture.',
    tech: ['Next.js', 'Supabase', 'AI APIs', 'TypeScript'],
    links: [{ type: 'web', url: 'https://nailingai.co/en/', label: 'Visit website' }],
  },
  {
    slug: 'avidef',
    title: 'AVIDEF',
    company: 'AVIDEF Consultancy',
    category: 'Corporate Website',
    year: '2024',
    gallery: g('avidef.png', 'avidef2.png', 'avidef3.png', 'avidef4.png'),
    short:
      'Corporate website & digital infrastructure for an Aerospace & Defense consultancy.',
    description:
      'I designed and developed the website and digital infrastructure for AVIDEF Consultancy — a firm specializing in quality management systems, supplier development and operational excellence in the Aerospace & Defense sectors — alongside corporate documentation and early-stage business structuring.',
    tech: ['React', 'Next.js', 'Tailwind CSS'],
    links: [{ type: 'web', url: 'https://avideftr.com/', label: 'Visit website' }],
  },
  {
    slug: 'vialess',
    title: 'Vialess',
    company: 'Vialess',
    category: 'Web & Mobile',
    year: '2025',
    gallery: g('vialess.png', 'vialess2.png', 'vialess3.png', 'vialess4.png'),
    short:
      'Admin dashboard for an eco-friendly digital business-card platform.',
    description:
      'Vialess is an innovative platform for modern, eco-friendly networking through digital business cards, helping users manage and share connections efficiently. I developed the admin dashboard — focusing on a fast, scalable and user-friendly interface for data management and administrative control.',
    tech: ['Next.js', 'TypeScript', 'Tailwind CSS', 'Supabase'],
    links: [
      { type: 'web', url: 'https://www.vialess.com.tr/', label: 'Visit website' },
      {
        type: 'ios',
        url: 'https://app.vialess.me/WfQk/yop39slc?_gl=1*39mdsr*_gcl_au*MjA5NTE3MDA0LjE3Nzg5MjY0MDA.',
        label: 'App Store',
      },
      {
        type: 'android',
        url: 'https://play.google.com/store/apps/details?id=com.vialess',
        label: 'Google Play',
      },
    ],
  },
  {
    slug: 'automotive-platform',
    title: 'Togg Media Center',
    company: 'Togg',
    category: 'Web App',
    year: '2025',
    gallery: g(
      'automotive.png',
      'automotive2.png',
      'automotive3.png',
      'automotive4.png'
    ),
    short:
      'Full-stack media-center web platform for the automotive sector.',
    description:
      'A full-stack web platform built from scratch for Togg’s media center. I architected the authentication and database layer with Supabase, developed admin panels and content management systems, implemented role-based access and transactional email flows, and led the front-end architecture with Next.js, React and Tailwind.',
    tech: ['Next.js', 'React', 'Tailwind CSS', 'Supabase'],
    links: [
      {
        type: 'web',
        url: 'https://togg-mediacenter.com/',
        label: 'Visit website',
      },
    ],
  },
  {
    slug: 'wugo',
    title: 'Wugo',
    company: 'Wugo',
    category: 'Web & Mobile',
    year: '2023',
    gallery: g('wugo.png', 'wugo2.png', 'wugo3.png', 'wugo4.png'),
    short:
      'Event organization platform — mobile & web features in production.',
    description:
      'Wugo is an event organization platform connecting companies and users. Working cross-functionally in a small team, I developed and maintained mobile and web features with React Native and React, assisted backend API integrations with Node.js/Nest.js, and implemented real-time Socket features — working with MySQL, Docker and TypeScript in a production environment.',
    tech: ['React Native', 'React', 'Node.js', 'Nest.js', 'MySQL'],
    links: [
      { type: 'web', url: 'https://www.wugo.app', label: 'Visit website' },
      { type: 'ios', url: 'https://wugo.go.link/l0blb', label: 'App Store' },
      { type: 'android', url: 'https://wugo.go.link/gaUHX', label: 'Google Play' },
    ],
  },
  {
    slug: 'bosnets',
    title: 'BosNets',
    company: 'BosNets',
    category: 'R&D',
    year: '2025',
    gallery: g('bosnets.png', 'bosnets2.png', 'bosnets3.png', 'bosnets4.png'),
    short:
      'Bluetooth-based device communication — mobile application layer.',
    description:
      'BosNets focuses on new communication technologies, developing devices and software for seamless communication. As part of a multidisciplinary team of 8 (including academic researchers and engineers), I develop the mobile application layer that enables Bluetooth-based device communication and integrate hardware-level protocols with the mobile interface.',
    tech: ['React Native', 'Bluetooth', 'Hardware Integration'],
    links: [{ type: 'web', url: 'https://www.bosnets.com/', label: 'Visit website' }],
  },
  {
    slug: '4ps-networks',
    title: '4Ps Networks',
    company: '4Ps Networks (later Vialess)',
    category: 'B2B Platform',
    year: '2022',
    gallery: g('4ps.png', '4ps2.png'),
    short:
      'Next-gen B2B platform — later rebranded Vialess (discontinued).',
    description:
      'A next-generation B2B platform designed to eliminate intermediaries and streamline business transactions. I built front-end components with React and Firebase infrastructure, and took part in project planning, budgeting and investor-facing preparations. The project was later rebranded as Vialess and eventually discontinued.',
    tech: ['React', 'Firebase', 'JavaScript'],
    links: [{ type: 'web', url: 'https://vialesss.web.app/', label: 'Visit website' }],
  },
  {
    slug: 'countdown-app',
    title: 'Countdown App',
    company: 'MOIC 2022',
    category: 'Web App',
    year: '2022',
    gallery: g('moic.png'),
    short:
      'Dual-language conference app for MOIC 2022 — 224 delegates, 56 countries.',
    description:
      'An application with dual-language support built with my teammate for the MOIC 2022 event, attended by 224 delegates from 56 countries. It includes functions such as timers, attendance tracking and committee polling used across the conference.',
    tech: ['React', 'JavaScript'],
    links: [
      {
        type: 'web',
        url: 'https://moic-countdown.netlify.app',
        label: 'Visit website',
      },
    ],
  },
  {
    slug: 'youtline',
    title: 'Youtline',
    company: 'Startup',
    category: 'MERN Stack',
    year: '2023',
    gallery: g('youtline.png', 'youtline2.png', 'youtline3.png'),
    short: 'Education-mentoring MERN-stack startup project.',
    description:
      'A MERN-stack application that was our start-up project in the education-mentoring field. Although we paused the project, it was a great opportunity to deepen the full MERN stack.',
    tech: ['MongoDB', 'Express', 'React', 'Node.js'],
    links: [
      { type: 'github', url: 'https://github.com/CodderEnesG/Youtline', label: 'View on GitHub' },
    ],
  },
  {
    slug: 'client-portfolio',
    title: 'Client Portfolio',
    company: 'Freelance',
    category: 'Web',
    year: '2024',
    gallery: [Nazif],
    short: 'A tailored portfolio site built to a client’s specific needs.',
    description:
      'A portfolio project tailored to the client’s specific needs, showcasing their work and skills with a personalized design and a clean, user-friendly interface.',
    tech: ['React', 'Styled Components'],
    links: [{ type: 'web', url: 'https://www.nazifgungor.com/', label: 'Visit website' }],
  },
  {
    slug: 'istanbul-app',
    title: 'Istanbul App',
    company: 'Personal',
    category: 'MERN Stack',
    year: '2022',
    gallery: [IstanbulApp],
    short: 'MERN app about historical places in Istanbul with auth & admin.',
    description:
      'A MERN project about historical places in Istanbul. It includes admin, login and pagination systems. Users can create an account via Google or the custom auth system and add their own posts.',
    tech: ['MongoDB', 'Express', 'React', 'Node.js'],
    links: [{ type: 'web', url: 'https://enesgungor.vercel.app', label: 'Visit website' }],
  },
  {
    slug: 'freelance-blog-app',
    title: 'Freelance Blog App',
    company: 'Freelance',
    category: 'Web',
    year: '2022',
    gallery: [Blog],
    short: 'Blog platform with a Strapi admin, deployed via Docker + Nginx.',
    description:
      'A freelance blog project developed with my teammate. We used React for the front-end and Strapi for the admin panel, then rented a virtual machine and deployed the project using Nginx and Docker.',
    tech: ['React', 'Strapi', 'Docker', 'Nginx'],
    links: [
      {
        type: 'github',
        url: 'https://github.com/CodderEnesG/Freelance-Blog-App',
        label: 'View on GitHub',
      },
    ],
  },
];

export const getProject = (slug) => projects.find((p) => p.slug === slug);

export default projects;
